/**
 * PURPOSE:
 * Adaptar la respuesta administrativa de la llave del evento 17 a una vista pública de solo lectura.
 *
 * RESPONSIBILITIES:
 * - Exponer únicamente rondas, partidos, slots, participantes y marcadores públicos.
 * - Ocultar datos administrativos como reemplazo, cupos pendientes y acciones disponibles.
 * - Mantener sourceMatchCode para dibujar conexiones reales entre partidos.
 *
 * COLLABORATORS:
 * - Event17PublicBracket.jsx
 * - Event17PublicBracketMatch.jsx
 * - Event17PublicBracketConnectors.jsx
 *
 * ROUTE:
 * src/components/Fixture/components/event17PublicBracket.mapper.js
 */

const toNumberOrNull = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const mapInstitution = (institution) => {
  if (!institution) return null;

  return {
    idinstitution: toNumberOrNull(institution.idinstitution),
    name: String(institution.name || "").trim() || "Por definir",
    // FTR-0006: abreviatura para moodName = 1 (displayName nunca la reemplaza).
    abrev: String(institution.abrev || "").trim(),
    logo: String(institution.logo || "").trim(),
  };
};

const mapParticipant = (participant) => {
  if (!participant) return null;

  const displayName =
    typeof participant.displayName === "string" && participant.displayName.trim() !== ""
      ? participant.displayName.trim()
      : null;

  return {
    idparticipant: toNumberOrNull(participant.idparticipant),
    idinstitution: toNumberOrNull(participant.idinstitution),
    // FTR-0006: override visual opcional del nombre completo del participante.
    displayName,
    institution: mapInstitution(participant.institution),
  };
};

const mapSlot = (slot) => ({
  side: Number(slot?.side) === 2 ? 2 : 1,
  sourceType: String(slot?.sourceType || "SEED"),
  sourceLabel: String(slot?.sourceLabel || "Por definir"),
  sourceMatchCode: slot?.sourceMatchCode
    ? String(slot.sourceMatchCode)
    : null,
  sourcePosition: toNumberOrNull(slot?.sourcePosition),
  participant: mapParticipant(slot?.participant),
});

const mapMatch = (match) => {
  const state = toNumberOrNull(match?.state) ?? 1;
  const isOfficial = state === 4;

  return {
    code: String(match?.code || ""),
    kind: String(match?.kind || "MATCH"),
    position: toNumberOrNull(match?.position),
    displayOrder: toNumberOrNull(match?.displayOrder),
    roundNumber: toNumberOrNull(match?.roundNumber),
    roundLabel: String(match?.roundLabel || ""),
    tagFinal: String(match?.tagFinal || ""),
    isOfficial,
    score: isOfficial && match?.score ? String(match.score) : null,
    slots: Array.isArray(match?.slots) ? match.slots.map(mapSlot) : [],
  };
};

const mapRound = (round) => ({
  number: toNumberOrNull(round?.number),
  key: String(round?.key || ""),
  label: String(round?.label || ""),
  matches: Array.isArray(round?.matches)
    ? round.matches.map(mapMatch)
    : [],
});

export function mapEvent17PublicBracket(rawData) {
  const rounds = Array.isArray(rawData?.rounds)
    ? rawData.rounds.map(mapRound)
    : [];

  return {
    hasBracket: Boolean(rawData?.hasBracket) && rounds.length > 0,
    eventId: toNumberOrNull(rawData?.eventId),
    idsport: toNumberOrNull(rawData?.idsport),
    sportKind: String(rawData?.sportKind || ""),
    participantCount: toNumberOrNull(rawData?.participantCount) ?? 0,
    rounds,
  };
}
